/**
 * "Acceder como Cliente" — impersonación real de sesión.
 *
 * Solo disponible para el super admin (ver `canPickEnv` / ENV_PICKER_EMAIL) en
 * el entorno de Producción. El backend (`os-impersonate-client`) valida quién
 * llama y devuelve un magic-link de un solo uso para el usuario destino; aquí
 * lo canjeamos por una sesión real, de modo que Smart Canvas OS se comporta
 * exactamente como lo hará para ese cliente (RLS incluida).
 *
 * La sesión del admin se guarda solo en `sessionStorage` de la pestaña y se
 * restaura al finalizar.
 */
import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";

const ADMIN_SESSION_KEY = "os-admin-session";
const IMPERSONATION_KEY = "os-client-impersonation";

export type ClientImpersonationState = {
  clientId: string;
  userId: string;
  email: string;
  companyName: string;
  logId: string | null;
  startedAt: string;
};

function readActive(): ClientImpersonationState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ClientImpersonationState) : null;
  } catch {
    return null;
  }
}

type Store = {
  active: ClientImpersonationState | null;
  setActive: (v: ClientImpersonationState | null) => void;
};

export const useClientImpersonation = create<Store>((set) => ({
  active: readActive(),
  setActive: (v) => set({ active: v }),
}));

export function isClientImpersonationActive(): boolean {
  return readActive() !== null;
}

export async function startClientImpersonation(clientId: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (!session) throw new Error("no_session");

  // Guardamos la sesión del admin ANTES de canjear el magic link.
  sessionStorage.setItem(
    ADMIN_SESSION_KEY,
    JSON.stringify({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    }),
  );

  let res: { data: any; error: any };
  try {
    res = await supabase.functions.invoke("os-impersonate-client", {
      body: { client_id: clientId },
    });
  } catch (e) {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    throw e;
  }
  if (res.error || !res.data?.token_hash) {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    throw new Error(res.data?.error ?? res.error?.message ?? "impersonation_failed");
  }

  const { error: otpErr } = await supabase.auth.verifyOtp({
    token_hash: res.data.token_hash as string,
    type: "magiclink",
  });
  if (otpErr) {
    // Recuperamos la sesión del admin si el canje falla.
    try {
      const raw = sessionStorage.getItem(ADMIN_SESSION_KEY);
      if (raw) await supabase.auth.setSession(JSON.parse(raw));
    } catch {
      /* noop */
    }
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    throw otpErr;
  }

  const state: ClientImpersonationState = {
    clientId,
    userId: res.data.target?.user_id ?? "",
    email: res.data.target?.email ?? "",
    companyName: res.data.target?.company_name ?? "",
    logId: res.data.log_id ?? null,
    startedAt: new Date().toISOString(),
  };
  sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
  useClientImpersonation.getState().setActive(state);

  // Recarga completa: garantiza que ninguna caché del admin sobreviva y que
  // toda la app arranque con la identidad del cliente.
  window.location.assign("/os/client");
}

export async function endClientImpersonation(): Promise<void> {
  const active = readActive();
  const raw = sessionStorage.getItem(ADMIN_SESSION_KEY);

  // Cierra la fila de auditoría con la sesión actual (aún la del cliente no
  // sirve: la función exige al super admin) → lo hacemos tras restaurar.
  sessionStorage.removeItem(IMPERSONATION_KEY);
  useClientImpersonation.getState().setActive(null);

  if (!raw) {
    await supabase.auth.signOut();
    window.location.assign("/os/login");
    return;
  }

  try {
    const tokens = JSON.parse(raw) as { access_token: string; refresh_token: string };
    const { error } = await supabase.auth.setSession(tokens);
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    if (error) throw error;
    if (active?.logId) {
      try {
        await supabase.functions.invoke("os-impersonate-client", {
          body: { action: "end", log_id: active.logId },
        });
      } catch {
        /* auditoría best-effort */
      }
    }
    window.location.assign("/os/admin/account");
  } catch {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    await supabase.auth.signOut();
    window.location.assign("/os/login");
  }
}
